import { ItemStatus } from "@focus/interfaces/lib/common/itemFetchStatus";
import type { FocusAjaxError, Duck } from "@focus/interfaces";
import { getErrorMessage } from "@focus/services/lib/helpers/errorParser";
import { of } from "rxjs";
import { ofType } from "redux-observable";
import { map, mergeMap, catchError, startWith } from "rxjs/operators";
import type { ColumnFiltersState } from "../helpers/pager";

export interface ConjunctionsFetcherConfig {
  storeId: string;
  computeURL: (any?: any) => string;
}

export default function createConjunctionsFetcher(
  config: ConjunctionsFetcherConfig
): Duck {
  const { storeId, computeURL } = config;

  // define constants
  const SET = `${storeId}/SET`;
  const SET_FILTER = `${storeId}/SET_FILTER`;
  const FETCH_START = `${storeId}/FETCH_START`;
  const FETCH_ERROR = `${storeId}/FETCH_ERROR`;
  const FETCH_PENDING = `${storeId}/FETCH_PENDING`;

  // ACTIONS
  function setItems(payload: any) {
    return { type: SET, payload };
  }
  function setFilter(payload: object) {
    return { type: SET_FILTER, payload };
  }
  function fetchItems(payload: any) {
    return { type: FETCH_START, payload };
  }
  function setError(payload: string) {
    return { type: FETCH_ERROR, payload };
  }
  function setPending() {
    return { type: FETCH_PENDING };
  }

  // REDUCER
  // default state
  type State = {
    items: any[];
    status: ItemStatus;
    errorMessage: string;
    filter: ColumnFiltersState;
  };

  const initialState: State = {
    items: [],
    status: ItemStatus.PENDING,
    errorMessage: "",
    filter: [],
  };

  /**
   * Reducer to handle the conjunction-related actions
   *
   * @param  {Object} state={}
   * @param  {Object} action
   */
  function reducer(state = initialState, action) {
    switch (action.type) {
      case SET_FILTER:
        // Update the filter information in the state
        return { ...state, filter: action.payload };
      case FETCH_PENDING:
        return {
          ...state,
          items: [],
          status: ItemStatus.PENDING,
          errorMessage: "",
        };
      case FETCH_ERROR:
        return {
          ...state,
          status: ItemStatus.ERROR,
          errorMessage: "",
          filter: {},
        };
      case SET:
        return {
          ...state,
          items: action.payload,
          status: ItemStatus.READY,
          errorMessage: "",
        };
      default:
        return state;
    }
  }

  const actionTypes = {
    SET_FILTER,
  };

  const actions = {
    setItems,
    setFilter,
    fetchItems,
  };

  const selectors = {
    getFilter: (state: State) => state.filter,
    getStatus: (state: State) => state.status,
    getError: (state: State) => state.errorMessage,
    getItems: (state: State) => state.items,
  };

  const fetchItemsEpic = (action$, _state$, { ajax }) => {
    return action$.pipe(
      ofType(FETCH_START),
      mergeMap((action) => {
        return ajax.getJSON(computeURL(action.payload)).pipe(
          map((response) => setItems(response)),
          catchError((error: FocusAjaxError) =>
            of(setError(getErrorMessage(error)))
          ),
          startWith(setPending())
        );
      })
    );
  };

  const middleware = [fetchItemsEpic];
  return { actions, reducer, middleware, selectors, actionTypes };
}
